import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Ship, Check } from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { TESTIDS } from "@/lib/testIds";
import { formatCalendarDate } from "@/lib/calendarDate";
import { formatVoyageIdentity } from "@/lib/voyageIdentity";

/**
 * CruiseSelector · CKB-040 v4 · LAW-005 · Organisation and Context
 * Separation.
 *
 * The Cruise Selector is the sole application-level mechanism through
 * which the traveller changes which cruise the app is contextually
 * viewing. Reads the portfolio from GET /api/cruises/portfolio and
 * writes the session-level `selectedCruiseId` (via CruiseContext). It
 * NEVER mutates `booking_status`, `booked_at`, `cancelled_at`,
 * `embark_date`, `disembark_date`, or the sort of `/portfolio` — those
 * belong to the lifecycle transitions (`/book`, `/cancel`, `/uncancel`).
 *
 * Rendered on every cruise-aware workspace via `TopHeader`.
 */
export default function CruiseSelector({ variant = "compact", onAddCruise } = {}) {
  const isWide = variant === "wide" || variant === "header";
  const isHeader = variant === "header";
  const {
    portfolio,
    selectedCruiseId,
    selectCruise,
    setRoute,
    firstRun,
  } = useCruise();

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Flat lookup: id → { row, section }.
  const rowsBySection = useMemo(() => {
    if (!portfolio) return { next: null, planning: [], future: [], completed: [], cancelled: [] };
    return {
      next: portfolio.next || null,
      planning: portfolio.planning?.items || [],
      future: portfolio.future?.items || [],
      completed: portfolio.archive?.completed?.items || [],
      cancelled: portfolio.archive?.cancelled?.items || [],
    };
  }, [portfolio]);

  const allRows = useMemo(() => {
    const rs = rowsBySection;
    return [rs.next, ...rs.planning, ...rs.future, ...rs.completed, ...rs.cancelled].filter(Boolean);
  }, [rowsBySection]);

  const selected = useMemo(
    () => allRows.find((v) => v?.id === selectedCruiseId) || rowsBySection.next || null,
    [allRows, selectedCruiseId, rowsBySection.next],
  );

  const totalCount = allRows.length;
  const selectedIdentity = useMemo(() => formatVoyageIdentity({
    ...selected,
    embark_date: selected?.embark_date || selected?.embark_at,
    disembark_date: selected?.disembark_date || selected?.disembark_at,
    cruise_line: selected?.cruise_line?.name || selected?.cruise_line_name,
  }), [selected]);

  // First-run (no cruises yet) — the CruisesWorkspace empty state owns
  // the sole "Create your first cruise" action, so we render nothing
  // here to avoid duplicating that CTA in the header.
  if (firstRun || (portfolio && totalCount === 0)) {
    return (
      <button
        type="button"
        onClick={() => onAddCruise ? onAddCruise() : setRoute("cruises/new")}
        className="flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-[13px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6DE5] md:w-auto"
        data-testid="cc-cruise-selector-empty"
      >
        <Ship className="h-4 w-4 text-[#1E6DE5]" />
        Select or add a cruise
      </button>
    );
  }

  const handleSelect = async (row) => {
    if (!row?.id) return;
    setOpen(false);
    if (row.id !== selectedCruiseId) {
      await selectCruise(row.id);
    }
  };

  const renderOption = (row, sectionKey) => {
    const isSelected = row.id === selectedCruiseId;
    const embark = row.embark_date || row.embark_at || null;
    const disembark = row.disembark_date || row.disembark_at || null;
    return (
      <li key={row.id}>
        <button
          type="button"
          onClick={() => handleSelect(row)}
          data-testid={TESTIDS.cruiseSelectorOption(row.id)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
            isSelected ? "bg-slate-100" : "hover:bg-slate-50"
          }`}
        >
          <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
            {row.cruise_line?.logo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${row.cruise_line.logo_url}`}
                alt=""
                className="w-6 h-6 object-contain select-none"
                draggable={false}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <Ship className="w-4 h-4 text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#0F172A] truncate">
              {row.name || row.ship_name || "Untitled cruise"}
            </div>
            <div className="text-[11px] text-[#64748B] truncate">
              {row.ship_name || "Ship TBD"}
              {embark && <> · {formatCalendarDate(embark)}{disembark ? ` – ${formatCalendarDate(disembark)}` : ""}</>}
            </div>
          </div>
          {isSelected && <Check className="w-3.5 h-3.5 text-[#1e6de5] shrink-0" />}
        </button>
      </li>
    );
  };

  const Group = ({ title, keyName, rows }) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="mt-2" data-testid={TESTIDS.cruiseSelectorGroup(keyName)}>
        <div className="px-3 pb-1 text-[10px] font-black uppercase tracking-[0.16em] text-[#94A3B8]">
          {title} <span className="text-[#CBD5E1]">· {rows.length}</span>
        </div>
        <ul className="space-y-0.5">{rows.map((r) => renderOption(r, keyName))}</ul>
      </div>
    );
  };

  return (
    <div className={`relative ${isHeader ? "w-full md:w-[clamp(24rem,38vw,36rem)] md:flex-none" : isWide ? "w-full md:w-auto" : ""}`} data-testid={TESTIDS.cruiseSelector} data-cruise-selector-variant={variant}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={TESTIDS.cruiseSelectorTrigger}
        className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all ${
          isWide ? "w-full max-w-none justify-start" : "max-w-[190px] sm:max-w-[280px]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
      >
        <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
          {selected?.cruise_line?.logo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${selected.cruise_line.logo_url}`}
              alt=""
              className="w-5 h-5 object-contain select-none"
              draggable={false}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <Ship className="w-3.5 h-3.5 text-slate-400" />
          )}
        </div>
        <div className={`min-w-0 text-left leading-tight ${isWide ? "flex-1" : ""}`}>
          <div className="text-[10px] uppercase tracking-[0.12em] font-black text-[#94A3B8]">
            Selected cruise
          </div>
          <div className={`text-[13px] font-semibold text-[#0F172A] ${isWide ? "truncate" : "truncate max-w-[110px] sm:max-w-[180px]"}`} data-testid="cc-cruise-selector-primary" title={selectedIdentity.primary}>
            {isWide && selected ? (
              selectedIdentity.primary
            ) : (
              selected?.name || selected?.ship_name || "Choose a cruise"
            )}
          </div>
          {isWide && selectedIdentity.dateRange && (
            <div className="mt-0.5 truncate text-[11px] font-medium text-[#64748B]" data-testid="cc-cruise-selector-dates" title={selectedIdentity.dateRange}>
              {selectedIdentity.dateRange}
            </div>
          )}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 self-center text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          ref={menuRef}
          className={`absolute mt-2 max-h-[70vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-2xl p-2 z-50 ${
            isWide ? "left-0 w-[min(340px,calc(100vw-1.5rem))] md:left-auto md:right-0" : "right-0 w-[min(340px,calc(100vw-1.5rem))]"
          }`}
          data-testid={TESTIDS.cruiseSelectorMenu}
          role="listbox"
        >
          {rowsBySection.next && (
            <Group
              title="Next Cruise"
              keyName="next"
              rows={[rowsBySection.next]}
            />
          )}
          <Group title="Planning" keyName="planning" rows={rowsBySection.planning} />
          <Group title="Future" keyName="future" rows={rowsBySection.future} />
          <Group title="Archive · Completed" keyName="completed" rows={rowsBySection.completed} />
          <Group title="Archive · Cancelled" keyName="cancelled" rows={rowsBySection.cancelled} />

          <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between px-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setRoute("voyages"); }}
              data-testid="cc-cruise-selector-manage"
              className="text-[11px] font-semibold text-[#1e6de5] hover:text-[#1857b5]"
            >
              Manage cruises →
            </button>
            {rowsBySection.next && selectedCruiseId !== rowsBySection.next.id && (
              <button
                type="button"
                data-testid={TESTIDS.cruiseSelectorReturnNext}
                onClick={async () => { setOpen(false); await selectCruise(rowsBySection.next.id); }}
                className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
              >
                Return to Next Cruise ↩
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
